/* eslint-disable sonarjs/cognitive-complexity */

import type { IOptions } from 'sanitize-html'
import sanitizeHtml from 'sanitize-html'
import { isSSR } from '../next/services/nextjs.rendering'

/**
 * Regular expressions for HTML parsing
 */
const HTML_TAG_REGEX = /<\/?([a-zA-Z0-9]+)([^>]*)>|([^<]+)/g
export const PLAIN_TEXT_REGEX = /<[^>]*>?/gm
export const RECIPIENT_NAME_REGEX = /^[A-Za-z-' âäàç&éêëèµöùüûôÉÀÈØÇÊÂÔÙïÏ]{1,30}$/u
export const ADDRESS_LINE_REGEX = /^[\p{L}0-9\s'.-]*$/u
export const QUOTE_NAME_REGEX = /^[\p{L}0-9 .<(+!&\]$*)\-/,%_>:@'"=£{}°#]+$/u
export const CUSTOM_TEXT_ENDING = '&hellip;&nbsp;'
export const DOT_SEPARATOR = '•'

// sanitize-html defaults strip class attributes, breaking CMS-authored inline styling
export const SANITIZE_HTML_OPTIONS: IOptions = {
  allowedTags: sanitizeHtml.defaults.allowedTags,
  allowedAttributes: {
    ...sanitizeHtml.defaults.allowedAttributes,
    '*': ['class'],
  },
}

/**
 * Base sanitize-html options for safe HTML rendering
 * Allows basic text formatting, headings, lists, and links
 */
export const BASE_SANITIZE_OPTIONS: IOptions = {
  allowedTags: [
    'a',
    'b',
    'sub',
    'sup',
    'i',
    'em',
    'strong',
    'h2',
    'h3',
    'h4',
    'p',
    'span',
    'ul',
    'ol',
    'li',
    'br',
  ],
  allowedAttributes: {
    a: ['href', 'class', 'title', 'target', 'rel'],
  },
  allowedSchemes: ['https'],
}

/**
 * Extracts plain text from HTML content.
 * Uses DOMParser on the client and regex on the server.
 *
 * @param html - The HTML string to extract text from.
 * @returns The extracted plain text.
 */
export const getPlainText = (html: string): string => {
  if (isSSR) {
    // Server-side: Use a regex as a fallback
    return html.replace(PLAIN_TEXT_REGEX, '')
  }

  const tmp = document.createElement('div')
  tmp.innerHTML = html

  return tmp.textContent || tmp.innerText || ''
}

/**
 * Removes trailing <br> tags (and variants) from an HTML string.
 */
export const trimTrailingBr = (html: string): string => html.replace(/(\s*<br\s*\/?>\s*)+$/, '')

/**
 * Sanitizes HTML, removes trailing <br>, extracts plain text, and trims whitespace.
 * Returns an empty string if the input is null/undefined/empty.
 */
export const getSanitizedPlainText = (
  html: string | null | undefined,
  options: IOptions,
): string => {
  if (!html) return ''

  return getPlainText(trimTrailingBr(sanitizeHtml(html, options))).trim()
}

/**
 * Custom function to safely truncate HTML without breaking tags.
 * It ensures that the truncated HTML is well-formed.
 *
 * @param html - The sanitized HTML string to truncate.
 * @param maxLength - The maximum number of characters for the text content.
 * @param addEllipsis - Whether to add ellipsis at the end of truncated text (default: true).
 * @returns The truncated HTML string with proper closing tags.
 */
export const truncateHtml = (html: string, maxLength: number, addEllipsis = true): string => {
  let totalLength = 0
  const stack: string[] = []
  let truncated = ''
  const regex = new RegExp(HTML_TAG_REGEX)
  let match: RegExpExecArray | null

  while ((match = regex.exec(html)) !== null) {
    if (match[1]) {
      // It's a tag
      const tag = match[0]
      const tagName = match[1].toLowerCase()

      if (tag.startsWith('</')) {
        // Closing tag
        const lastTag = stack.pop()

        if (lastTag === tagName) {
          truncated += tag
        }
      } else if (tag.endsWith('/>')) {
        // Self-closing tag
        truncated += tag
      } else {
        // Opening tag
        stack.push(tagName)
        truncated += tag
      }
    } else if (match[3]) {
      // It's text
      const text = match[3]

      if (totalLength + text.length > maxLength) {
        const remaining = maxLength - totalLength
        truncated += text.slice(0, remaining)

        if (addEllipsis) {
          truncated += CUSTOM_TEXT_ENDING
        }

        break
      } else {
        truncated += text
        totalLength += text.length
      }
    }
  }

  // Close any remaining open tags
  while (stack.length > 0) {
    const tag = stack.pop()

    if (tag) {
      truncated += `</${tag}>`
    }
  }

  return truncated
}
